import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FinanceSlider } from '@/components/finance/FinanceSlider';
import { gpPerHour } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/gp-per-hour — the GP per Hour tool (F-013 S6.6). Gross profit on a billable hour,
 * how utilisation moves it, and a utilisation × charge-out matrix. A calculator over your inputs.
 */
export const Route = createFileRoute('/finance/forecast_/gp-per-hour')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <GpPerHour />
    </FinanceGuard>
  ),
});


const UTILS = [60, 70, 80, 90];

function GpPerHour() {
  const [chargeOut, setChargeOut] = useState(150);
  const [cost, setCost] = useState(55);
  const [util, setUtil] = useState(80);
  const [hours, setHours] = useState(160);

  const rates = [chargeOut - 20, chargeOut, chargeOut + 20, chargeOut + 40];
  const base = gpPerHour(chargeOut, cost, util, hours);

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <h1 className="ds-h1 mt-1">GP per Hour</h1>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">What each hour actually earns you — and how utilisation moves it as much as the rate does.</p>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Inputs</p>
          <div className="flex flex-col gap-2.5">
            <FinanceSlider label="Charge-out rate ($/hr)" value={chargeOut} set={setChargeOut} min={60} max={400} step={5} fmt={(v) => `$${v}`} />
            <FinanceSlider label="Cost of the hour ($/hr)" value={cost} set={setCost} min={20} max={150} step={1} fmt={(v) => `$${v}`} />
            <FinanceSlider label="Utilisation (%)" value={util} set={setUtil} min={40} max={100} step={5} fmt={(v) => `${v}%`} />
            <FinanceSlider label="Available hours / mo" value={hours} set={setHours} min={80} max={250} step={5} fmt={(v) => `${v} h`} />
          </div>
          <div className="mt-3 rounded-xl border border-primary/40 bg-primary/5 p-3">
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">GP per billable hour</span>
            <span className={`ds-kpi-md ${base.gpPerBillableHour >= 0 ? 'text-foreground' : 'text-danger'}`}>{fmtCurrency(base.gpPerBillableHour)}</span>
            <span className="mt-1 block text-[11.5px] text-muted-foreground">{fmtCurrency(base.effectiveGpPerHour)}/available hr · {Math.round(base.billableHours)} billable hrs/mo · {fmtCurrency(base.annualGp)}/yr</span>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-border bg-card p-4 lg:col-span-2">
          <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Annual GP · utilisation × charge-out rate</p>
          <table className="w-full text-[12px]">
            <thead><tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
              <th className="px-2 py-1.5 text-left font-bold">Util \ Rate</th>
              {rates.map((r) => <th key={r} className="px-2 py-1.5 text-right font-bold">{fmtCurrency(r)}</th>)}
            </tr></thead>
            <tbody>
              {UTILS.map((u) => (
                <tr key={u} className="border-t border-border/60">
                  <td className="px-2 py-1.5 font-semibold text-foreground">{u}%</td>
                  {rates.map((r) => {
                    const g = gpPerHour(r, cost, u, hours).annualGp;
                    const maxAbs = Math.max(...UTILS.flatMap((u2) => rates.map((r2) => Math.abs(gpPerHour(r2, cost, u2, hours).annualGp))), 1);
                    const alpha = 0.08 + 0.32 * (Math.abs(g) / maxAbs); // heat, scaled to this grid
                    return (
                      <td key={r} className={`px-2 py-1.5 text-right font-mono tabular-nums ${g < 0 ? 'text-danger' : 'text-foreground'}`}
                        style={{ background: `hsl(var(${g < 0 ? '--danger' : '--success'}) / ${alpha.toFixed(2)})` }}>
                        {fmtCurrency(g)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-muted-foreground">Each cell = annual gross profit at that utilisation and rate, at your current cost/hr. Lifting utilisation 10 points often beats a rate rise.</p>
        </div>
      </div>
      <ForecastToolNav current="gp-per-hour" />
    </div>
  );
}
